import React, { useContext, useEffect, useState } from 'react'
import {
    Button,
    TextField,
    CircularProgress,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Typography,
    Autocomplete,
} from '@mui/material'

import UserSearchSelect from '../manage-users/UserSearchSelect'
import userRoles from '../../lib/userRoles'
import { domainService } from '../../lib/services'
import { useSnackbar } from 'notistack'
import { DomainContext } from '../contexts/DomainContext'
import { isEmpty } from 'lodash'

const AddUserForm = props => {
    const {
        enableSearch,
        noUsersOption,
        values: {
            firstName,
            lastName,
            userId,
            permission,
            email,
            phone,
            isExternal,
            passwordExpiryInDays,
            canPublish,
            canApprove,
            companyId,
            domainId,
        },
        errors,
        touched,
        disabledFields = [],
        handleSubmit,
        handleChange,
        setFieldValue,
        handleBlur,
        isValid,
        isSubmitting,
        formClasses,
        buttonText = 'Save',
        requireContext = true,
        operation,
    } = props

    const { enqueueSnackbar: showSnackbar } = useSnackbar()
    const [company, setCompany] = useState(null)
    const [domain, setDomain] = useState(null)
    const [domainsByCompanyId, setDomainsByCompanyId] = useState([])
    const { companies } = useContext(DomainContext)

    useEffect(() => {
        setDomain(null)
        if (companyId) {
            const company = companies?.find(c => c.id === companyId)
            setCompany(company)
            domainService
                .getDomainsByCompanyId(companyId)
                .then(data => {
                    setDomainsByCompanyId(data ?? [])
                    if (data?.length) onDomainChange({}, data[0])
                })
                .catch(e =>
                    showSnackbar('An error occurred fetching Companies', { variant: 'error', preventDuplicate: true })
                )
        }
    }, [companyId])

    const handleSearchChange = e => {
        handleChange(e)
        handleBlur(e)
    }

    const onCompanyChange = (e, company) => {
        setCompany(company)
        setFieldValue('companyId', company?.id ?? '')
        handleBlur(e)
    }

    const onDomainChange = (e, domain) => {
        setDomain(domain)
        setFieldValue('domainId', domain?.id ?? '')
        handleBlur(e)
    }

    return (
        <form className={formClasses.form} onSubmit={handleSubmit}>
            {enableSearch && <UserSearchSelect onChange={handleSearchChange} noUsersOption={noUsersOption} />}
            <div className={formClasses.fieldWrapper}>
                <TextField
                    name="firstName"
                    disabled={disabledFields.includes('firstName')}
                    helperText={touched.firstName ? errors.firstName : ''}
                    error={touched.firstName && Boolean(errors.firstName)}
                    label="First Name"
                    value={firstName || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    margin="normal"
                />
                <TextField
                    name="lastName"
                    disabled={disabledFields.includes('lastName')}
                    helperText={touched.lastName ? errors.lastName : ''}
                    error={touched.lastName && Boolean(errors.lastName)}
                    label="Last Name"
                    value={lastName || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    margin="normal"
                />
                {requireContext && (
                    <TextField
                        name="permission"
                        select
                        helperText={touched.permission ? errors.permission : ''}
                        error={touched.permission && Boolean(errors.permission)}
                        label="Role"
                        value={permission}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        margin="normal"
                    >
                        {Object.keys(userRoles)
                            .filter(user => user !== '6') // remove owner
                            .map(key => (
                                <MenuItem key={key} value={key}>
                                    {userRoles[key]}
                                </MenuItem>
                            ))}
                    </TextField>
                )}
                <TextField
                    name="userId"
                    disabled={disabledFields.includes('userId')}
                    helperText={touched.userId ? errors.userId : ''}
                    error={Boolean(errors.userId)}
                    label="Username"
                    value={userId || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    margin="normal"
                />
                <TextField
                    name="email"
                    disabled={disabledFields.includes('email')}
                    helperText={touched.email ? errors.email : ''}
                    error={touched.email && Boolean(errors.email)}
                    label="Email Address"
                    value={email || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    margin="normal"
                />
                <TextField
                    name="phone"
                    disabled={disabledFields.includes('phone')}
                    helperText={touched.phone ? errors.phone : ''}
                    error={touched.phone && Boolean(errors.phone)}
                    label="Phone Number"
                    value={phone || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    margin="normal"
                />
                <TextField
                    name="passwordExpiryInDays"
                    disabled={disabledFields.includes('passwordExpiryInDays') || isExternal}
                    helperText={touched.passwordExpiryInDays ? errors.passwordExpiryInDays : ''}
                    error={touched.passwordExpiryInDays && Boolean(errors.passwordExpiryInDays)}
                    label="Password Expiry (Days)"
                    value={!isExternal && passwordExpiryInDays ? passwordExpiryInDays : ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    margin="normal"
                    type="number"
                />

                <div>
                    <FormControlLabel
                        control={
                            <Checkbox
                                disabled={disabledFields.includes('isExternal')}
                                checked={isExternal}
                                onChange={handleChange}
                                color="primary"
                                name="isExternal"
                                id="isExternal"
                            />
                        }
                        label="Use External Login"
                    />
                    {isExternal && !disabledFields.includes('isExternal') ? (
                        <Typography variant="caption" color="primary" display="block">
                            Make sure username matches external login
                        </Typography>
                    ) : null}
                </div>

                <Autocomplete
                    key="companyId"
                    size="small"
                    value={company}
                    options={companies}
                    getOptionLabel={option => option.name}
                    onBlur={handleBlur}
                    disabled={operation === 'addExistingUser' && !isEmpty(firstName) && !isEmpty(company)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={params => (
                        <TextField
                            {...params}
                            helperText={touched.companyId ? errors.companyId : ''}
                            error={Boolean(errors.companyId)}
                            label="Company"
                            variant="outlined"
                            name="companyId"
                        />
                    )}
                    onChange={onCompanyChange}
                ></Autocomplete>

                <Autocomplete
                    key="domainId"
                    size="small"
                    value={domain}
                    options={domainsByCompanyId}
                    getOptionLabel={option => option.name}
                    onBlur={handleBlur}
                    disabled={operation === 'addExistingUser' && !isEmpty(firstName) && !isEmpty(domain)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={params => (
                        <TextField
                            {...params}
                            helperText={touched.domainId ? errors.domainId : ''}
                            error={Boolean(errors.domainId)}
                            label="Domain"
                            variant="outlined"
                            name="domainId"
                        />
                    )}
                    onChange={onDomainChange}
                ></Autocomplete>

                <FormControlLabel
                    control={
                        <Checkbox
                            disabled={disabledFields.includes('canPublish')}
                            checked={canPublish}
                            onChange={handleChange}
                            color="primary"
                            name="canPublish"
                            id="canPublish"
                        />
                    }
                    label="Can Publish Scenario"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            disabled={disabledFields.includes('canApprove')}
                            checked={canApprove}
                            onChange={handleChange}
                            color="primary"
                            name="canApprove"
                            id="canApprove"
                        />
                    }
                    label="Can Approve Scenario"
                />
            </div>

            <div className={formClasses.buttonWrapper}>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    className={formClasses.submit}
                    disabled={!isValid || isSubmitting}
                >
                    {isSubmitting ? <CircularProgress size={24} className={formClasses.buttonProgress} /> : buttonText}
                </Button>
            </div>
        </form>
    )
}

export default AddUserForm
